import { useState } from 'react'

import { Button, Link } from '@atoms'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@atoms/DropdownMenu'
import AppErrorsAlert from '@molecules/Alerts/AppErrorsAlert'
import InfoCard from '@molecules/InfoCard'
import { Slot } from '@hooks/useSlots'
import { scopedTranslation } from '@utils/I18n'
import type { GroupShowQueryType } from '@frontend/graphql/queries/groups'

import GroupCriteria from '../list-groups/GroupCriteria'
import PointPeople from '../list-groups/PointPeople'
import LeaderNames from '../list-groups/LeaderNames'
import { GroupDetailsPanel } from '../list-groups/GroupDetailsPanel'
import type { SharedInertiaProps, RouteProps } from '../../types/groupTypes'
import { useDialog } from '@components/DialogProvider'
import GroupDeleteDialog from './GroupDeleteDialog'
import MailerSenderDialog from './MailerSenderDialog'

const t = scopedTranslation('components')

type GroupCardType = {
  group: GroupShowQueryType['group']
  globalMailerSender: string
  totalCount: number
}

export default function GroupCard({
  globalMailerSender,
  group,
  permissions,
  routes,
  totalCount,
}: GroupCardType & SharedInertiaProps & RouteProps) {
  const { open: openDialog } = useDialog()
  const {
    canEditGroup,
    canEditGroupPublicDescription,
    canSyncGroup,
    canExportGroupSupporters,
    canImportSupporters,
    canDestroyGroup,
    canEditMailerSender,
  } = permissions
  const { id, name, status, publicUrl, publicPublish, description, pointPeople, notifyAllAdmins, captains, criteria } =
    group

  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [mailerSenderModalOpen, setMailerSenderModalOpen] = useState(false)

  return (
    <>
      <AppErrorsAlert errorsPath="showGroupCardErrors" />
      <InfoCard dataCompId={'group-details-card'} data-testid={`group-card-${name}`}>
        {description && (
          <Slot name="heading">
            <p data-testid="group-description" className="tw:m-0 tw:text-gray-900 tw:whitespace-pre-wrap">
              {description}
            </p>
          </Slot>
        )}

        <Slot name="main">
          <GroupDetailsPanel>
            <GroupCriteria criteria={criteria} />

            <LeaderNames leaderType="captains" leaders={captains.nodes} />

            <PointPeople
              notifyAllAdmins={Boolean(notifyAllAdmins)}
              pointPersonEmails={pointPeople.nodes.map((pp) => pp.email)}
            />
          </GroupDetailsPanel>
        </Slot>

        <Slot name="actions">
          <Slot name="primary">
            <div className="tw:flex tw:flex-wrap tw:gap-2">
              {canEditGroup && (
                <>
                  <Link
                    size="sm"
                    rank="link"
                    leadingIcon="pencil-02"
                    ariaLabel={t('group_card.aria_labels.edit_group', { name })}
                    href={`/groups/${id}/edit?ref=${window.location.pathname + window.location.search}`}
                    as="inertiaLink"
                  >
                    {t('group_card.buttons.edit_group')}
                  </Link>
                  <Link
                    size="sm"
                    rank="link"
                    leadingIcon="pencil-02"
                    ariaLabel={t('group_card.aria_labels.edit_group_criteria', { name })}
                    href={`/groups/${id}/criteria/edit?ref=${window.location.pathname + window.location.search}`}
                    as="inertiaLink"
                  >
                    {t('group_card.buttons.edit_group_criteria')}
                  </Link>
                  <Link
                    size="sm"
                    rank="link"
                    leadingIcon="users-01"
                    ariaLabel={t('group_card.aria_labels.manage_leaders', {
                      name,
                    })}
                    href={`/groups/${id}/leaders?ref=${window.location.pathname + window.location.search}`}
                    as="inertiaLink"
                  >
                    {t('group_card.buttons.manage_leaders')}
                  </Link>
                </>
              )}

              <Link
                disabled={status === 'hidden' || !publicUrl}
                href={publicUrl ?? ''}
                leadingIcon="eye"
                size="sm"
                rank="link"
                ariaLabel={t('group_card.aria_labels.public_page', { name })}
                dataCompId={'group-details-card_view-public-page'}
              >
                {t('group_card.buttons.public_page')}
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    rank="secondary"
                    size="sm"
                    trailingIcon={'chevron-down'}
                    ariaLabel={t('group_card.aria_labels.more_actions', { name })}
                  >
                    {t('group_card.buttons.more_actions')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="tw:flex tw:flex-col tw:gap-1 tw:px-1 tw:py-1">
                  <DropdownMenuItem asChild>
                    <Link
                      size="sm"
                      rank="link"
                      leadingIcon="funnel"
                      ariaLabel={t('group_card.aria_labels.advanced_filters', { name })}
                      href={routes.advancedFiltersRoute}
                      dataCompId={'group-details-card_advanced_filters'}
                    >
                      {t('group_card.buttons.advanced_filters')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      size="sm"
                      rank="link"
                      leadingIcon="user-x"
                      ariaLabel={t('group_card.aria_labels.supporter_contact_preferences', { name })}
                      href={routes.optOutsRoute}
                      dataCompId={'group-details-card_supporter_contact_preferences'}
                    >
                      {t('group_card.buttons.supporter_contact_preferences')}
                    </Link>
                  </DropdownMenuItem>

                  {canExportGroupSupporters && totalCount > 0 && (
                    <DropdownMenuItem asChild>
                      <Button
                        size="sm"
                        rank="link"
                        leadingIcon="download-cloud"
                        dataCompId={'group-details-card_export'}
                        ariaLabel={t('group_card.aria_labels.export', { name })}
                        onClick={() =>
                          openDialog({
                            type: 'supporterExport',
                            dialogData: { groupId: group.id, matchingCount: totalCount, totalCount },
                          })
                        }
                      >
                        {t('group_card.buttons.export')}
                      </Button>
                    </DropdownMenuItem>
                  )}

                  {canImportSupporters && (
                    <DropdownMenuItem asChild>
                      <Link
                        size="sm"
                        rank="link"
                        leadingIcon="upload-cloud-02"
                        href={routes.newImportRoute}
                        as="inertiaLink"
                        ariaLabel={t('group_card.aria_labels.import_supporters', { name })}
                        dataCompId={'group-details-card_import-supporters'}
                      >
                        {t('group_card.buttons.import_supporters')}
                      </Link>
                    </DropdownMenuItem>
                  )}

                  {canEditGroupPublicDescription && publicPublish && (
                    <DropdownMenuItem asChild>
                      <Button
                        size="sm"
                        rank="link"
                        leadingIcon="pencil-02"
                        ariaLabel={t('group_card.aria_labels.edit_public_page', { name })}
                        onClick={() =>
                          openDialog({
                            type: 'editGroupPublicDescription',
                            dialogData: {
                              groupId: id,
                              groupName: name,
                              publicDescription: group.publicDescription,
                              publicUrl: publicUrl,
                            },
                          })
                        }
                        dataCompId={'group-details-card_edit-public-page'}
                      >
                        {t('group_card.buttons.edit_public_page')}
                      </Button>
                    </DropdownMenuItem>
                  )}

                  {canEditMailerSender && (
                    <DropdownMenuItem asChild>
                      <Button
                        size="sm"
                        rank="link"
                        leadingIcon="mail-01"
                        ariaLabel={t('group_card.aria_labels.set_group_sender_email', { name })}
                        dataCompId={'group-details-mailer_sender'}
                        onClick={() => {
                          setMailerSenderModalOpen(true)
                        }}
                      >
                        {t('group_card.buttons.set_group_sender_email')}
                      </Button>
                    </DropdownMenuItem>
                  )}

                  {canSyncGroup && (
                    <DropdownMenuItem asChild>
                      <Link
                        size="sm"
                        rank="link"
                        leadingIcon="refresh"
                        href={`/groups/${id}/sync`}
                        as="inertiaLink"
                        dataCompId={'group-details-card_sync-supporters'}
                      >
                        {t('group_card.buttons.sync')}
                      </Link>
                    </DropdownMenuItem>
                  )}

                  {canDestroyGroup && (
                    <DropdownMenuItem asChild>
                      <Button
                        size="sm"
                        rank="link"
                        color="danger"
                        leadingIcon="trash-01"
                        onClick={() => {
                          setOpenConfirmation(true)
                        }}
                        ariaLabel={t('group_card.aria_labels.archive_group', { name })}
                      >
                        {t('group_card.buttons.archive_group')}
                      </Button>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <GroupDeleteDialog open={openConfirmation} onOpenChange={setOpenConfirmation} group={group} />
              <MailerSenderDialog
                open={mailerSenderModalOpen}
                onOpenChange={setMailerSenderModalOpen}
                groupId={id}
                mailerSender={
                  group.mailerSender && Boolean(group.mailerSender) ? group.mailerSender : globalMailerSender
                }
              />
            </div>
          </Slot>
        </Slot>
      </InfoCard>
    </>
  )
}
